import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import {
  SITE_URL,
  SITE_NAME,
  SITE_DESCRIPTION,
  SITE_LOCALE,
  SITE_LANGUAGE,
} from '../config/site';
import {
  buildOrganizationSchema,
  buildWebSiteSchema,
  buildLocalBusinessSchema,
} from '../utils/structuredData';

const DEFAULT_SEO = {
  siteName: SITE_NAME,
  title: `${SITE_NAME} | AI Solutions & Enterprise Software Philippines`,
  description: SITE_DESCRIPTION,
  keywords:
    'AI solutions Philippines, enterprise software, AI integration, chatbot development, digital transformation, IT consultancy Pampanga, business automation, AI consulting, AI products Philippines',
  url: SITE_URL,
  image: `${SITE_URL}/og-image.png`,
  twitterHandle: '@xdigitalph',
  locale: SITE_LOCALE,
  type: 'website',
};

function getCanonicalUrl(pathname) {
  const base = SITE_URL.replace(/\/$/, '');
  if (!pathname || pathname === '/') return `${base}/`;
  return `${base}${pathname.startsWith('/') ? pathname : `/${pathname}`}`;
}

export function SEO({
  title,
  description,
  keywords,
  image,
  url,
  path,
  type = 'website',
  noindex = false,
  structuredData = null,
  includeGlobalSchema = true,
  children,
}) {
  const location = useLocation();
  const canonicalUrl = url || getCanonicalUrl(path || location.pathname);

  const seo = {
    title: title ? `${title} | ${DEFAULT_SEO.siteName}` : DEFAULT_SEO.title,
    description: description || DEFAULT_SEO.description,
    keywords: keywords || DEFAULT_SEO.keywords,
    image: image || DEFAULT_SEO.image,
    url: canonicalUrl,
    type,
  };

  const globalSchema = includeGlobalSchema
    ? [buildOrganizationSchema(), buildWebSiteSchema(), buildLocalBusinessSchema()]
    : [];

  const pageSchema = structuredData
    ? Array.isArray(structuredData)
      ? structuredData
      : [structuredData]
    : [];

  const schemaItems = [...globalSchema, ...pageSchema];

  return (
    <Helmet htmlAttributes={{ lang: SITE_LANGUAGE }}>
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      <meta name="keywords" content={seo.keywords} />
      <meta name="author" content={SITE_NAME} />
      <link rel="canonical" href={seo.url} />

      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta
          name="robots"
          content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        />
      )}
      <meta
        name="googlebot"
        content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
      />

      <meta property="og:type" content={seo.type} />
      <meta property="og:url" content={seo.url} />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:image" content={seo.image} />
      <meta property="og:site_name" content={DEFAULT_SEO.siteName} />
      <meta property="og:locale" content={DEFAULT_SEO.locale} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={seo.url} />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seo.image} />
      <meta name="twitter:site" content={DEFAULT_SEO.twitterHandle} />
      <meta name="twitter:creator" content={DEFAULT_SEO.twitterHandle} />

      <meta name="theme-color" content="#6366f1" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content={DEFAULT_SEO.siteName} />

      <meta name="geo.region" content="PH" />
      <meta name="geo.placename" content="Pampanga, Philippines" />

      <meta httpEquiv="content-language" content={SITE_LANGUAGE} />
      <link rel="alternate" hrefLang={SITE_LANGUAGE} href={seo.url} />
      <link rel="alternate" hrefLang="x-default" href={seo.url} />
      <link rel="alternate" type="text/plain" href={`${SITE_URL}/llms.txt`} title="LLM-readable site summary" />

      {schemaItems.map((item, index) => (
        <script key={`schema-${index}`} type="application/ld+json">
          {JSON.stringify(item)}
        </script>
      ))}

      {children}
    </Helmet>
  );
}

export const PAGE_SEO = {
  home: {
    title: 'AI Solutions & Enterprise Software',
    description:
      'Transform your business with practical AI solutions. XDigital offers 11 AI-powered products, custom chatbots, automation, and digital transformation services in Pampanga, Philippines.',
    keywords:
      'AI solutions Philippines, enterprise AI software, AI products, chatbot development, automation, Pampanga IT company',
    path: '/',
  },
  about: {
    title: 'About Us',
    description:
      'Learn about XDigital IT Consultancy — a Philippine AI solutions provider helping enterprises, SMEs, and government adopt practical AI since 2020.',
    keywords: 'about XDigital, AI company Philippines, IT consultancy Pampanga',
    path: '/about',
  },
  products: {
    title: 'AI-Powered Products',
    description:
      'Explore 11 AI-powered enterprise products: Budget Management, Audit Management, Real Estate Valuation, Report Generator, Document Management, Tracker, AI Agents, Chatbot, and Project Management.',
    keywords:
      'AI products Philippines, budget management AI, audit software, AI chatbot, AI receptionist, enterprise software',
    path: '/products',
  },
  services: {
    title: 'Our Services',
    description:
      'AI implementation, infrastructure setup, analytics, RPA, custom AI development, and cloud security services for Philippine businesses.',
    keywords: 'AI services, IT consulting, software development, chatbot services, automation services Philippines',
    path: '/services',
  },
  invest: {
    title: 'Investment Opportunity',
    description:
      'Investment overview for XDigital IT Consultancy — AI-powered enterprise products serving banks, healthcare, logistics, and government in the Philippines.',
    keywords: 'AI investment Philippines, enterprise software investment, XDigital invest',
    path: '/invest',
  },
  contact: {
    title: 'Contact Us',
    description:
      'Contact XDigital IT Consultancy at sales@xdigital.com.ph for AI consultations, product demos, and enterprise software inquiries in the Philippines.',
    keywords: 'contact XDigital, AI consultation, IT services inquiry, schedule demo Philippines',
    path: '/contact',
  },
};

export function generateBreadcrumbData(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export function generateFAQData(faqItems) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

export function generateServiceData(service) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: service.type,
    name: service.name,
    description: service.description,
    provider: {
      '@type': 'Organization',
      name: SITE_NAME,
    },
    areaServed: {
      '@type': 'Country',
      name: 'Philippines',
    },
  };
}

export default SEO;
